import { Easing, Tween } from "@tweenjs/tween.js";
import Hammer from "hammerjs";

import { DomUtil } from "@/shared/ui/Helper";

export interface IMomentumScrollerConfig {
    axis?: string;
    restrictTo?: JQuery;
}

export class MomentumScroller {
    private stopThresholdDefault = 0.3;
    private bounceDeceleration = 0.08;
    private bounceAcceleration = 0.1;
    private friction = 0.95;
    private velocitySpeed = 17;

    private container: JQuery;

    private currentPosition?: { x: number; y: number };
    private lastPointer?: { x: number; y: number };
    private currentDeaceleration?: { x: number; y: number };

    public boundsX?: { min: number; max: number };
    public boundsY?: { min: number; max: number };

    private rendering = false;
    public isDragging = false; //user is dragging
    public isDeacelerating = false; //animation when drag released
    private runningTween: any;

    private touchCtrl: HammerManager;

    private cfg: IMomentumScrollerConfig;

    constructor(container: JQuery, cfg?: IMomentumScrollerConfig) {
        this.container = container;

        this.cfg = {
            axis: undefined,
            restrictTo: undefined,
        };
        this.cfg = { ...this.cfg, ...cfg };

        if (this.cfg.restrictTo !== undefined) this.updateBounds();

        this.touchCtrl = new Hammer(this.container[0], {
            recognizers: [
                // a finger never holds perfectly still on a tap, so at threshold 0 every tap dragged
                // the content a few px and the click handler then animated it back - Hammer's default
                // of 10 keeps a tap a tap. panstart reads the pointer once the threshold is passed, so
                // a real drag still tracks the finger from the first moved pixel
                [Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 10 }],
                [Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 20 }],
            ],
            domEvents: false,
        });

        this.touchCtrl.on("panstart", (ev) => {
            if (this.runningTween !== undefined) {
                this.runningTween.stop();
                this.runningTween = undefined;
            }

            this.isDragging = true;
            this.onStart(ev);
        });

        this.touchCtrl.on("panmove", (ev) => {
            if (this.rendering) return;

            this.requestAnimFrame(() => {
                this.onMove(ev);
                this.rendering = false;
            });

            this.rendering = true;
        });

        this.touchCtrl.on("panend", (ev) => {
            this.onEnd(ev);

            setTimeout(() => {
                this.isDragging = false;
            }, 120);
        });
    }

    // Hammer listens on the window too, so a scroller whose element goes away has to let go of it
    public destroy() {
        this.touchCtrl.destroy();
    }

    public scrollTo(x: number, y: number) {
        this.isDeacelerating = false;

        this.runningTween = new Tween(DomUtil.translatePosition(this.container))
            .to({ x: x, y: y }, 200)
            .easing(Easing.Quadratic.Out)
            .onUpdate((f) => {
                this.container.css({ transform: "translate3d(" + f.x + "px, " + f.y + "px, 0)" });
                this.currentPosition = f;
            })
            .start();

        this.runningTween.onComplete(() => {
            this.runningTween = undefined;
        });

        const animate = (time?: null) => {
            if (this.runningTween !== undefined && this.runningTween.isPlaying()) {
                this.runningTween.update(time);
                this.requestAnimFrame(animate);
            }
        };

        animate();
    }

    public updateBounds() {
        this.container.css("position", "absolute");
        if (this.cfg.restrictTo !== undefined) {
            if (this.cfg.axis !== undefined && this.cfg.axis === "x") {
                let min = this.container.outerWidth(true) - this.cfg.restrictTo.width();
                if (min < 0) min = 0;

                this.boundsX = { min: min * -1, max: 0 };
            } else this.boundsX = undefined;

            if (this.cfg.axis !== undefined && this.cfg.axis === "y") {
                let min = this.container.outerHeight(true) - this.cfg.restrictTo.height();
                if (min < 0) min = 0;

                this.boundsY = { min: min * -1, max: 0 };
            } else this.boundsY = undefined;
        } else {
            this.boundsX = undefined;
            this.boundsY = undefined;
        }
        this.container.css("position", "relative");
    }

    private onStart(ev: HammerInput) {
        this.isDeacelerating = false;
        this.currentPosition = DomUtil.translatePosition(this.container);
        this.lastPointer = ev.center;
    }

    private onMove(ev: HammerInput) {
        const pointerDelta = { x: ev.center.x - this.lastPointer!.x, y: ev.center.y - this.lastPointer!.y };
        const newPosition = { x: this.currentPosition!.x, y: this.currentPosition!.y };
        if (this.cfg.axis === undefined || this.cfg.axis === "x") newPosition.x += pointerDelta.x;

        if (this.cfg.axis === undefined || this.cfg.axis === "y") newPosition.y += pointerDelta.y;

        const diff = this.checkBounds(newPosition);
        if (diff.x !== 0) newPosition.x -= pointerDelta.x * this.dragOutOfBoundsMultiplier(diff.x);

        if (diff.y !== 0) newPosition.y -= pointerDelta.y * this.dragOutOfBoundsMultiplier(diff.y);

        this.container.css({ transform: "translate3d(" + newPosition.x + "px, " + newPosition.y + "px, 0)" });
        this.currentPosition = newPosition;

        this.lastPointer = ev.center;
    }

    private onEnd(ev: HammerInput) {
        if (this.currentDeaceleration === undefined) this.currentDeaceleration = { x: 0, y: 0 };

        this.currentDeaceleration.x += ev.velocityX * this.velocitySpeed;
        this.currentDeaceleration.y += ev.velocityX * this.velocitySpeed;

        this.isDeacelerating = true;
        this.animateDeacelleration();
    }

    private checkBounds(position: { x: number; y: number }) {
        let xDiff = 0;
        let yDiff = 0;

        if (this.boundsX !== undefined) {
            if (position.x < this.boundsX.min) xDiff = this.boundsX.min - position.x;
            else if (position.x > this.boundsX.max) xDiff = this.boundsX.max - position.x;
        }

        if (this.boundsY !== undefined) {
            if (position.y < this.boundsY.min) yDiff = this.boundsY.min - position.y;
            else if (position.y > this.boundsY.max) yDiff = this.boundsY.max - position.y;
        }

        return {
            x: xDiff,
            y: yDiff,
            inBounds: xDiff === 0 && yDiff === 0,
        };
    }

    private dragOutOfBoundsMultiplier(val: number): number {
        return 0.000005 * Math.pow(val, 2) + 0.0001 * val + 0.55;
    }

    private animateDeacelleration() {
        if (!this.isDeacelerating) return;

        this.currentDeaceleration = {
            x: this.currentDeaceleration!.x * this.friction,
            y: this.currentDeaceleration!.y * this.friction,
        };

        const isXAxis = this.cfg.axis === undefined || this.cfg.axis === "x";
        const isYAxis = this.cfg.axis === undefined || this.cfg.axis === "y";

        if (isXAxis) {
            this.currentPosition!.x += this.currentDeaceleration.x;
        }

        if (isYAxis) {
            this.currentPosition!.y += this.currentDeaceleration.y;
        }

        const diff = this.checkBounds(this.currentPosition!);
        if (
            (isXAxis && Math.abs(this.currentDeaceleration.x) > this.stopThresholdDefault) ||
            (isYAxis && Math.abs(this.currentDeaceleration.y) > this.stopThresholdDefault) ||
            !diff.inBounds
        ) {
            const reboundAdjust = 2.5;

            if (diff.x !== 0) {
                if (diff.x * this.currentDeaceleration.x <= 0) {
                    this.currentDeaceleration.x += diff.x * this.bounceDeceleration;
                    if (this.currentDeaceleration.x > -1 && this.currentDeaceleration.x < 1)
                        this.currentDeaceleration.x = 0;
                } else {
                    const adjust = diff.x > 0 ? reboundAdjust : -reboundAdjust;
                    this.currentDeaceleration.x = (diff.x + adjust) * this.bounceAcceleration;
                }
            }
            if (diff.y !== 0) {
                if (diff.y * this.currentDeaceleration.y <= 0)
                    this.currentDeaceleration.y += diff.y * this.bounceDeceleration;
                else {
                    const adjust = diff.y > 0 ? reboundAdjust : -reboundAdjust;
                    this.currentDeaceleration.y = (diff.y + adjust) * this.bounceAcceleration;
                }
            }

            this.container.css({
                transform: "translate3d(" + this.currentPosition!.x + "px, " + this.currentPosition!.y + "px, 0)",
            });
            this.requestAnimFrame(() => this.animateDeacelleration());
        } else {
            this.currentDeaceleration = undefined;
            this.isDeacelerating = false;
        }
    }

    private requestAnimFrame(handler: { () }) {
        (
            window.requestAnimationFrame ||
            window["webkitRequestAnimationFrame"] ||
            window["mozRequestAnimationFrame"] ||
            function (callback) {
                window.setTimeout(callback, 1000 / 60);
            }
        )(handler);
    }
}
